import { getStatus, normalizeOperationOutcome } from '@medplum/core';
import { Bundle, OperationOutcome, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSubscriptionWithLimitHandling } from '@/services/subscriptions';
import { trimStringValues } from '@bariendo/lib/services';

/** Computes one edit's change from the Patient it is applied to. */
export type PatientEdit = (current: Patient) => Partial<Patient>;

export interface UsePatientResult {
  patient: Patient | undefined;
  patientLoading: boolean;
  patientError: OperationOutcome | Error | undefined;
  refreshPatient: () => Promise<void>;
  updatePatient: (updates: Partial<Patient>) => Promise<void>;
  /**
   * Saves one field-level edit with a version check. The edit is applied to the
   * last Patient the server returned and written with If-Match on its version.
   * On a 412 the Patient is re-read, the same edit is applied to the fresh copy,
   * and the write is retried once. A second 412, or any other failure, throws.
   *
   * Unlike `updatePatient`, whose callers pass whole arrays built from the
   * Patient they rendered, the edit function recomputes its change from
   * whatever copy it is given, so a retry never writes stale data back.
   */
  applyPatientEdit: (edit: PatientEdit) => Promise<void>;
}

function versionHeaders(patient: Patient): RequestInit | undefined {
  const versionId = patient.meta?.versionId;
  return versionId ? { headers: { 'If-Match': `W/"${versionId}"` } } : undefined;
}

export function usePatient(patientId: string | undefined): UsePatientResult {
  const medplum = useMedplum();
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientError, setPatientError] = useState<OperationOutcome | Error | undefined>(undefined);

  // What is shown is the last Patient the server returned with every unsaved
  // edit applied on top. An edit leaves the list when its write settles, so a
  // failure rolls back only that edit and a success shows the server's copy.
  const serverPatient = useRef<Patient | undefined>(undefined);
  const pendingEdits = useRef(new Map<number, PatientEdit>());
  const nextEditId = useRef(0);
  // The route does not remount on a patient switch, so a write for the previous
  // patient can settle after the next one has loaded. Its result is dropped.
  const currentPatientId = useRef(patientId);

  const show = useCallback(() => {
    const server = serverPatient.current;
    setPatient(
      server &&
        [...pendingEdits.current.values()].reduce<Patient>((shown, edit) => ({ ...shown, ...edit(shown) }), server)
    );
  }, []);

  /** Takes a Patient from the server unless it is older than the one already held. */
  const acceptServerPatient = useCallback(
    (incoming: Patient) => {
      if (incoming.id !== currentPatientId.current) return;
      const held = serverPatient.current;
      const older =
        held !== undefined &&
        held.id === incoming.id &&
        (incoming.meta?.lastUpdated ?? '') < (held.meta?.lastUpdated ?? '');
      if (!older) serverPatient.current = incoming;
      show();
    },
    [show]
  );

  const loadPatient = useCallback(async () => {
    if (!patientId) return;

    setPatientLoading(true);
    setPatientError(undefined);
    try {
      acceptServerPatient(await medplum.readResource('Patient', patientId));
    } catch (e) {
      serverPatient.current = undefined;
      setPatient(undefined);
      setPatientError(e as OperationOutcome | Error);
    } finally {
      setPatientLoading(false);
    }
  }, [patientId, medplum, acceptServerPatient]);

  /** Shows `edit` until `write` settles, then drops it; a failed write rethrows. */
  const withPendingEdit = useCallback(
    async (edit: PatientEdit, write: () => Promise<Patient>) => {
      const id = nextEditId.current++;
      pendingEdits.current.set(id, edit);
      show();
      try {
        const saved = await write();
        pendingEdits.current.delete(id);
        acceptServerPatient(saved);
      } catch (e) {
        pendingEdits.current.delete(id);
        show();
        throw e;
      }
    },
    [show, acceptServerPatient]
  );

  const updatePatient = useCallback(
    async (updates: Partial<Patient>) => {
      if (!patient) return;
      const cleanedUpdates = trimStringValues(updates);
      await withPendingEdit(
        () => cleanedUpdates,
        () => medplum.updateResource({ ...patient, ...cleanedUpdates })
      );
    },
    [patient, medplum, withPendingEdit]
  );

  const applyPatientEdit = useCallback(
    async (edit: PatientEdit) => {
      const base = serverPatient.current;
      if (!base?.id) return;
      const cleanEdit: PatientEdit = (current) => trimStringValues(edit(current));

      await withPendingEdit(cleanEdit, async () => {
        try {
          return await medplum.updateResource({ ...base, ...cleanEdit(base) }, versionHeaders(base));
        } catch (e) {
          if (getStatus(normalizeOperationOutcome(e)) !== 412) throw e;
          const fresh = await medplum.readResource('Patient', base.id as string, { cache: 'no-cache' });
          acceptServerPatient(fresh);
          return medplum.updateResource({ ...fresh, ...cleanEdit(fresh) }, versionHeaders(fresh));
        }
      });
    },
    [medplum, withPendingEdit, acceptServerPatient]
  );

  useEffect(() => {
    if (currentPatientId.current !== patientId) {
      currentPatientId.current = patientId;
      serverPatient.current = undefined;
      pendingEdits.current.clear();
    }
    loadPatient();
  }, [patientId, loadPatient]);

  useSubscriptionWithLimitHandling(`Patient?_id=${patientId}`, (bundle: Bundle) => {
    const updatedPatient = bundle.entry?.[1]?.resource as Patient | undefined;
    if (updatedPatient) {
      acceptServerPatient(updatedPatient);
    }
  });

  return {
    patient,
    patientLoading,
    patientError,
    refreshPatient: loadPatient,
    updatePatient,
    applyPatientEdit,
  };
}
